import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useUnsavedChanges from '../hooks/useUnsavedChanges';

const API = 'https://carriera-backend.onrender.com/api';

// The 4th column now holds the whole post-placement lifecycle.
const COLUMNS = [
  { key: 'profile_prepared',    label: 'Profile Prepared',    statuses: ['profile_prepared'],    color: 'text-purple-600', dot: 'bg-purple-500' },
  { key: 'profile_sent',        label: 'Profile Sent',        statuses: ['profile_sent'],        color: 'text-blue-600',   dot: 'bg-blue-500' },
  { key: 'interview_scheduled', label: 'Interview Scheduled', statuses: ['interview_scheduled'], color: 'text-amber-600',  dot: 'bg-amber-500' },
  { key: 'placement',           label: 'Placed & Onboarding', statuses: ['placed', 'started', 'invoiced', 'completed', 'dropped_out'], color: 'text-green-600', dot: 'bg-green-500' },
];

// Lifecycle order used for the progress strip on placed cards.
const LIFECYCLE = ['placed', 'started', 'invoiced', 'completed'];
const LIFECYCLE_LABELS = { placed: 'Placed', started: 'Started', invoiced: 'Invoiced', completed: 'Completed' };
const PLACEMENT_STAGE = ['placed', 'started', 'invoiced']; // still has actions to take

const BLANK_INTERVIEW = {
  interview_date: '', interview_time: '', interview_type: 'face_to_face',
  location_or_link: '', contact_person: '', contact_department: '',
  documents: '', interview_notes: '', send_email: true
};

const BLANK_INVOICE = { fee_basis: 'percent', fee_percent: '', fee_amount: '', invoice_number: '', invoice_sent_date: '' };

// Extra fields captured at the Placed step (the JTK report + billing draw on these).
const BLANK_PLACE = {
  offer_letter_date: '', monthly_salary: '',
  fee_basis: 'flat', fee_amount: '', fee_percent: '',
  ic_number: '', candidate_address: ''
};

// Drop-out modal: how the drop-out is resolved.
const BLANK_DROPOUT = {
  dropout_date: '', dropout_reason: '',
  resolution: 'replacement',           // 'replacement' | 'refund' | 'none'
  replacement_candidate_id: '',
  refund_amount: '', refund_reason: ''
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'pm' : 'am'}`;
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

// Interview round helpers (rounds come embedded on each sheet from the list API)
function roundsOf(sheet) { return sheet.interview_rounds || []; }
function currentRoundNumber(sheet) {
  const rs = roundsOf(sheet).filter(r => r.status !== 'cancelled');
  return rs.length ? Math.max(...rs.map(r => r.round_number)) : 1;
}
function scheduledRound(sheet) {
  const rs = roundsOf(sheet).filter(r => r.status === 'scheduled');
  return rs.sort((a, b) => b.round_number - a.round_number)[0] || null;
}

// Guarantee badge text + tone for a placed card
function guaranteeBadge(sheet) {
  if (sheet.status === 'dropped_out') return { text: 'Dropped out — replacement / refund due', tone: 'red' };
  if (sheet.status === 'completed') return { text: 'Guarantee passed', tone: 'green' };
  if (!sheet.guarantee_end_date) return null;
  const d = daysUntil(sheet.guarantee_end_date);
  if (d == null) return null;
  if (d < 0) return { text: 'Guarantee ended — ready to complete', tone: 'gray' };
  return { text: `Guarantee ends in ${d} day${d === 1 ? '' : 's'} · ${formatDate(sheet.guarantee_end_date)}`, tone: 'amber' };
}

export default function JobSheets() {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  // Lookups
  const [allCandidates, setAllCandidates] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [clientLocations, setClientLocations] = useState([]);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedSecondaryJob, setSelectedSecondaryJob] = useState(null);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [jobSearch, setJobSearch] = useState('');
  const [creating, setCreating] = useState(false);

  // Change-position modal (reassign the role, same employer, pre-placement only)
  const [positionModal, setPositionModal] = useState({ open: false, sheet: null });
  const [positionMode, setPositionMode] = useState('existing'); // 'existing' | 'new'
  const [positionJobId, setPositionJobId] = useState('');
  const [positionNewTitle, setPositionNewTitle] = useState('');
  const [submittingPosition, setSubmittingPosition] = useState(false);

  // Interview modal — mode: 'schedule' | 'reschedule' | 'next_round'
  const [interviewModal, setInterviewModal] = useState({ open: false, sheet: null, mode: 'schedule' });
  const [interviewForm, setInterviewForm] = useState(BLANK_INTERVIEW);
  const [submittingInterview, setSubmittingInterview] = useState(false);

  // Cancel interview modal
  const [cancelModal, setCancelModal] = useState({ open: false, sheet: null });
  const [cancelSendEmail, setCancelSendEmail] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [submittingCancel, setSubmittingCancel] = useState(false);

  // Round outcome modal (silent — no emails)
  const [roundModal, setRoundModal] = useState({ open: false, sheet: null, round: null });
  const [roundOutcome, setRoundOutcome] = useState('passed');
  const [roundFeedback, setRoundFeedback] = useState('');
  const [submittingRound, setSubmittingRound] = useState(false);

  // Outcome (place / reject) modal
  const [outcomeModal, setOutcomeModal] = useState({ open: false, sheet: null, type: '' });
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [startDate, setStartDate] = useState('');
  const [offerPackage, setOfferPackage] = useState('');
  const [guaranteeMonths, setGuaranteeMonths] = useState('3');
  const [guaranteeUnit, setGuaranteeUnit] = useState('months');
  const [placeForm, setPlaceForm] = useState(BLANK_PLACE);
  const [outcomeSendEmail, setOutcomeSendEmail] = useState(false);
  const [submittingOutcome, setSubmittingOutcome] = useState(false);

  // "Placed in which position?" resolver (for cards carrying a + b, or a different role)
  const [placedChoice, setPlacedChoice] = useState('primary'); // 'primary'|'secondary'|'both'|'other'
  const [placedOtherMode, setPlacedOtherMode] = useState('existing'); // 'existing'|'new'
  const [placedOtherJobId, setPlacedOtherJobId] = useState('');
  const [placedOtherTitle, setPlacedOtherTitle] = useState('');

  // Hiring company at placement (client itself, a subsidiary, or a new one) + client terms
  const [placeClient, setPlaceClient] = useState(null); // full client record (subsidiaries, placement_terms)
  const [hiringChoice, setHiringChoice] = useState('client'); // 'client' | '<subsidiary index>' | 'new'
  const [newSub, setNewSub] = useState({ name: '', ssm_number: '', address: '' });

  // Post-placement modals
  const [startedModal, setStartedModal] = useState({ open: false, sheet: null });
  const [actualStartDate, setActualStartDate] = useState('');
  const [invoiceModal, setInvoiceModal] = useState({ open: false, sheet: null });
  const [invoiceForm, setInvoiceForm] = useState(BLANK_INVOICE);
  const [paymentModal, setPaymentModal] = useState({ open: false, sheet: null });
  const [paidDate, setPaidDate] = useState('');
  const [dropoutModal, setDropoutModal] = useState({ open: false, sheet: null });
  const [dropoutForm, setDropoutForm] = useState(BLANK_DROPOUT);
  const [submittingLifecycle, setSubmittingLifecycle] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const { markClean, confirmClose } = useUnsavedChanges();

  const fetchSheets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/job-sheets?archived=${showArchived}`, { headers });
      setSheets(data);
    } catch (err) {
      console.error('Failed to fetch job sheets:', err);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { fetchSheets(); }, [fetchSheets]);

  async function openCreateModal() {
    setShowCreate(true);
    setCreateStep(1);
    setSelectedCandidate(null);
    setSelectedJob(null);
    setSelectedSecondaryJob(null);
    setCandidateSearch('');
    setJobSearch('');
    markClean({ candidate: null, job: null });
    if (!allCandidates.length) {
      const { data } = await axios.get(`${API}/candidates`, { headers });
      setAllCandidates(data);
    }
    if (!allJobs.length) {
      const { data } = await axios.get(`${API}/jobs`, { headers });
      setAllJobs(data);
    }
  }

  async function handleCreate() {
    if (!selectedCandidate || !selectedJob) return;
    setCreating(true);
    try {
      await axios.post(`${API}/job-sheets`, {
        candidate_id: selectedCandidate.id,
        job_id: selectedJob.id,
        secondary_job_id: selectedSecondaryJob?.id || null
      }, { headers });
      setShowCreate(false);
      fetchSheets();
    } catch (err) {
      alert('Failed to create job sheet.');
    } finally {
      setCreating(false);
    }
  }

  async function openPositionModal(sheet) {
    setPositionModal({ open: true, sheet });
    setPositionMode('existing');
    setPositionJobId('');
    setPositionNewTitle('');
    // Ensure the jobs list is loaded for the same-employer dropdown.
    if (!allJobs.length) {
      try {
        const { data } = await axios.get(`${API}/jobs`, { headers });
        setAllJobs(data);
      } catch { /* dropdown just shows none; the "add new" path still works */ }
    }
  }

  async function submitPosition() {
    const { sheet } = positionModal;
    if (positionMode === 'new' ? !positionNewTitle.trim() : !positionJobId) {
      alert(positionMode === 'new' ? 'Enter the new position title.' : 'Select a position.');
      return;
    }
    const payload = positionMode === 'new'
      ? { new_job_title: positionNewTitle.trim() }
      : { new_job_id: positionJobId };
    setSubmittingPosition(true);
    try {
      await axios.patch(`${API}/job-sheets/${sheet.id}/change-position`, payload, { headers });
      setPositionModal({ open: false, sheet: null });
      fetchSheets();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to change position.');
    } finally {
      setSubmittingPosition(false);
    }
  }

  async function openInterviewModal(sheet, mode = 'schedule') {
    setInterviewModal({ open: true, sheet, mode });
    const clientId = sheet.jobs?.client_id;

    if (mode === 'reschedule') {
      const next = {
        interview_date: sheet.interview_date || '',
        interview_time: sheet.interview_time?.substring(0, 5) || '',
        interview_type: sheet.interview_type || 'face_to_face',
        location_or_link: sheet.location_or_link || '',
        contact_person: sheet.contact_person || '',
        contact_department: sheet.contact_department || '',
        documents: Array.isArray(sheet.documents) ? sheet.documents.join('\n') : (sheet.documents || ''),
        interview_notes: sheet.interview_notes || '',
        send_email: true
      };
      setInterviewForm(next);
      markClean(next);
    } else {
      // schedule / next_round → prefill from client defaults
      if (clientId) {
        try {
          const { data: client } = await axios.get(`${API}/clients/${clientId}`, { headers });
          const next = {
            ...BLANK_INTERVIEW,
            contact_person: client.interview_contact_name || '',
            contact_department: client.interview_contact_department || '',
            documents: Array.isArray(client.interview_documents)
              ? client.interview_documents.filter(Boolean).join('\n')
              : ''
          };
          setInterviewForm(next);
          markClean(next);
        } catch {
          setInterviewForm(BLANK_INTERVIEW);
          markClean(BLANK_INTERVIEW);
        }
      } else {
        setInterviewForm(BLANK_INTERVIEW);
        markClean(BLANK_INTERVIEW);
      }
    }

    if (clientId) {
      axios.get(`${API}/client-locations/${clientId}`, { headers })
        .then(r => setClientLocations(r.data))
        .catch(() => setClientLocations([]));
    }
  }

  async function submitInterview() {
    const { sheet, mode } = interviewModal;
    const { interview_date, interview_time, interview_type } = interviewForm;
    if (!interview_date || !interview_time || !interview_type) {
      alert('Date, time and type are required.');
      return;
    }
    setSubmittingInterview(true);
    try {
      const endpoint = mode === 'reschedule' ? 'reschedule'
        : mode === 'next_round' ? 'interviews/next-round'
        : 'schedule-interview';
      const payload = {
        ...interviewForm,
        documents: interviewForm.documents.split('\n').filter(Boolean)
      };
      await axios.patch(`${API}/job-sheets/${sheet.id}/${endpoint}`, payload, { headers });
      setInterviewModal({ open: false, sheet: null, mode: 'schedule' });
      fetchSheets();
    } catch (err) {
      alert('Failed to save interview.');
    } finally {
      setSubmittingInterview(false);
    }
  }

  function openCancelModal(sheet) {
    setCancelModal({ open: true, sheet });
    setCancelSendEmail(false);
    setCancelReason('');
    markClean({ sendEmail: false, reason: '' });
  }

  async function submitCancel() {
    const { sheet } = cancelModal;
    setSubmittingCancel(true);
    try {
      await axios.patch(`${API}/job-sheets/${sheet.id}/cancel-interview`,
        { send_email: cancelSendEmail, cancel_reason: cancelReason }, { headers });
      setCancelModal({ open: false, sheet: null });
      fetchSheets();
    } catch {
      alert('Failed to cancel interview.');
    } finally {
      setSubmittingCancel(false);
    }
  }

  function openRoundModal(sheet) {
    const round = scheduledRound(sheet);
    if (!round) { alert('No scheduled interview round to record an outcome for.'); return; }
    setRoundModal({ open: true, sheet, round });
    setRoundOutcome('passed');
    setRoundFeedback('');
  }

  async function submitRoundOutcome() {
    const { sheet, round } = roundModal;
    setSubmittingRound(true);
    try {
      await axios.patch(`${API}/job-sheets/${sheet.id}/interviews/${round.id}/outcome`,
        { outcome: roundOutcome, feedback: roundFeedback }, { headers });
      setRoundModal({ open: false, sheet: null, round: null });
      fetchSheets();
    } catch {
      alert('Failed to record outcome.');
    } finally {
      setSubmittingRound(false);
    }
  }

  function openOutcomeModal(sheet, type) {
    setOutcomeModal({ open: true, sheet, type });
    setOutcomeNotes('');
    setStartDate('');
    setOfferPackage('');
    setGuaranteeMonths('3');
    setGuaranteeUnit('months');
    setPlaceForm(BLANK_PLACE);
    setOutcomeSendEmail(false);
    setPlacedChoice('primary');
    setPlacedOtherMode('existing');
    setPlacedOtherJobId('');
    setPlacedOtherTitle('');
    setPlaceClient(null);
    setHiringChoice('client');
    setNewSub({ name: '', ssm_number: '', address: '' });
    markClean({ notes: '', startDate: '', offerPackage: '' });

    // Load jobs for the "a different position" picker on the placement step.
    if (type === 'place' && !allJobs.length) {
      axios.get(`${API}/jobs`, { headers })
        .then(({ data }) => setAllJobs(data))
        .catch(() => {});
    }

    // Load the client to show its placement terms + subsidiaries at placement.
    if (type === 'place' && sheet.jobs?.client_id) {
      axios.get(`${API}/clients/${sheet.jobs.client_id}`, { headers })
        .then(({ data }) => setPlaceClient(data))
        .catch(() => {});
    }

    // Pre-fill the candidate's IC + full address if we already have them, so
    // they don't have to be re-typed for the JTK report.
    if (type === 'place' && sheet.candidate_id) {
      axios.get(`${API}/candidates/${sheet.candidate_id}`, { headers })
        .then(({ data }) => setPlaceForm(f => ({
          ...f,
          ic_number: data.ic_number || '',
          candidate_address: data.address || ''
        })))
        .catch(() => {});
    }
  }

  function openEditPlacement(sheet) {
    setOutcomeModal({ open: true, sheet, type: 'edit' });
    setOutcomeNotes(sheet.outcome_notes || '');
    setStartDate(sheet.start_date || '');
    setOfferPackage(sheet.offer_package || '');
    setGuaranteeMonths(sheet.guarantee_months != null ? String(sheet.guarantee_months) : '3');
    setGuaranteeUnit(sheet.guarantee_unit || 'months');
    setPlaceForm({
      offer_letter_date: sheet.offer_letter_date || '',
      monthly_salary: sheet.monthly_salary != null ? String(sheet.monthly_salary) : '',
      fee_basis: sheet.fee_basis || 'flat',
      fee_amount: sheet.fee_amount != null ? String(sheet.fee_amount) : '',
      fee_percent: sheet.fee_percent != null ? String(sheet.fee_percent) : '',
      ic_number: '', candidate_address: ''
    });
    setOutcomeSendEmail(false);
    // Position defaults to "keep" so a routine edit never changes the role.
    setPlacedChoice('keep');
    setPlacedOtherMode('existing');
    setPlacedOtherJobId('');
    setPlacedOtherTitle('');
    // Hiring company: prefill from the placement's existing snapshot.
    setNewSub({ name: sheet.hiring_entity_name || '', ssm_number: sheet.hiring_entity_ssm || '', address: sheet.hiring_entity_address || '' });
    setPlaceClient(null);
    setHiringChoice('client');
    markClean({ notes: sheet.outcome_notes || '', startDate: sheet.start_date || '', offerPackage: sheet.offer_package || '' });
    if (!allJobs.length) {
      axios.get(`${API}/jobs`, { headers }).then(({ data }) => setAllJobs(data)).catch(() => {});
    }
    if (sheet.jobs?.client_id) {
      axios.get(`${API}/clients/${sheet.jobs.client_id}`, { headers })
        .then(({ data }) => {
          setPlaceClient(data);
          const subs = data.subsidiaries || [];
          if (sheet.hiring_entity_name) {
            const idx = subs.findIndex(s => (s?.name || '').trim().toLowerCase() === sheet.hiring_entity_name.trim().toLowerCase());
            setHiringChoice(idx >= 0 ? String(idx) : 'new');
          } else {
            setHiringChoice('client');
          }
        })
        .catch(() => {});
    }
    if (sheet.candidate_id) {
      axios.get(`${API}/candidates/${sheet.candidate_id}`, { headers })
        .then(({ data }) => setPlaceForm(f => ({
          ...f, ic_number: data.ic_number || '', candidate_address: data.address || ''
        })))
        .catch(() => {});
    }
  }

  async function submitOutcome() {
    const { sheet, type } = outcomeModal;
    const isPlaceOrEdit = type === 'place' || type === 'edit';
    if (isPlaceOrEdit && placedChoice === 'other'
      && (placedOtherMode === 'new' ? !placedOtherTitle.trim() : !placedOtherJobId)) {
      alert('Choose or enter the position the candidate was placed in.');
      return;
    }
    if (isPlaceOrEdit && hiringChoice === 'new' && !newSub.name.trim()) {
      alert('Enter the subsidiary name, or pick the client / an existing subsidiary.');
      return;
    }
    // Position change payload (shared by place + edit). 'keep'/absent = no change.
    const posFields = isPlaceOrEdit ? {
      placed_choice: placedChoice,
      ...(placedChoice === 'other'
        ? (placedOtherMode === 'new'
            ? { placed_job_title: placedOtherTitle.trim() }
            : { placed_job_id: placedOtherJobId })
        : {})
    } : {};
    // Hiring company snapshot (client itself when 'client').
    let hiringFields = {};
    if (isPlaceOrEdit) {
      if (hiringChoice === 'new') {
        hiringFields = {
          hiring_entity_name: newSub.name.trim(),
          hiring_entity_ssm: newSub.ssm_number.trim(),
          hiring_entity_address: newSub.address.trim(),
          save_subsidiary: true
        };
      } else if (hiringChoice !== 'client') {
        const s = (placeClient?.subsidiaries || [])[Number(hiringChoice)];
        if (s) hiringFields = {
          hiring_entity_name: s.name || '',
          hiring_entity_ssm: s.ssm_number || '',
          hiring_entity_address: s.address || ''
        };
      }
      // hiringChoice === 'client' → send nothing → parent client used (or cleared on edit)
    }
    setSubmittingOutcome(true);
    try {
      if (type === 'edit') {
        await axios.patch(`${API}/job-sheets/${sheet.id}/placement-details`, {
          start_date: startDate || null,
          offer_package: offerPackage,
          offer_letter_date: placeForm.offer_letter_date || null,
          monthly_salary: placeForm.monthly_salary,
          guarantee_months: guaranteeMonths ? parseInt(guaranteeMonths, 10) : null,
          guarantee_unit: guaranteeUnit,
          fee_basis: 'flat',
          fee_amount: placeForm.fee_amount,
          fee_percent: null,
          ic_number: placeForm.ic_number || null,
          candidate_address: placeForm.candidate_address || null,
          ...posFields,
          ...hiringFields
        }, { headers });
      } else {
        await axios.patch(`${API}/job-sheets/${sheet.id}/${type}`, {
          outcome_notes: outcomeNotes,
          ...(type === 'place' ? {
            start_date: startDate || null,
            offer_package: offerPackage,
            offer_letter_date: placeForm.offer_letter_date || null,
            monthly_salary: placeForm.monthly_salary,
            guarantee_months: guaranteeMonths ? parseInt(guaranteeMonths, 10) : null,
            guarantee_unit: guaranteeUnit,
            fee_basis: 'flat',
            fee_amount: placeForm.fee_amount,
            fee_percent: null,
            ic_number: placeForm.ic_number || null,
            candidate_address: placeForm.candidate_address || null,
            ...posFields,
            ...hiringFields
          } : {}),
          send_email: outcomeSendEmail
        }, { headers });
      }
      setOutcomeModal({ open: false, sheet: null, type: '' });
      fetchSheets();
    } catch {
      alert('Failed to update outcome.');
    } finally {
      setSubmittingOutcome(false);
    }
  }

  // ── Post-placement lifecycle actions ──
  function openStartedModal(sheet) {
    setStartedModal({ open: true, sheet });
    setActualStartDate(sheet.start_date || todayStr());
  }
  async function submitStarted() {
    setSubmittingLifecycle(true);
    try {
      await axios.patch(`${API}/job-sheets/${startedModal.sheet.id}/confirm-started`,
        { actual_start_date: actualStartDate || null }, { headers });
      setStartedModal({ open: false, sheet: null });
      fetchSheets();
    } catch { alert('Failed to confirm start.'); }
    finally { setSubmittingLifecycle(false); }
  }

  function openInvoiceModal(sheet) {
    setInvoiceModal({ open: true, sheet });
    setInvoiceForm({ ...BLANK_INVOICE, invoice_sent_date: todayStr() });
  }
  async function submitInvoice() {
    setSubmittingLifecycle(true);
    try {
      await axios.patch(`${API}/job-sheets/${invoiceModal.sheet.id}/invoice`, invoiceForm, { headers });
      setInvoiceModal({ open: false, sheet: null });
      fetchSheets();
    } catch { alert('Failed to record invoice.'); }
    finally { setSubmittingLifecycle(false); }
  }

  function openPaymentModal(sheet) {
    setPaymentModal({ open: true, sheet });
    setPaidDate(todayStr());
  }
  async function submitPayment() {
    setSubmittingLifecycle(true);
    try {
      await axios.patch(`${API}/job-sheets/${paymentModal.sheet.id}/payment`,
        { payment_status: 'paid', paid_date: paidDate || null }, { headers });
      setPaymentModal({ open: false, sheet: null });
      fetchSheets();
    } catch { alert('Failed to record payment.'); }
    finally { setSubmittingLifecycle(false); }
  }

  function openDropoutModal(sheet) {
    setDropoutModal({ open: true, sheet });
    setDropoutForm({ ...BLANK_DROPOUT, dropout_date: todayStr() });
  }
  async function submitDropout() {
    const id = dropoutModal.sheet.id;
    const { resolution, dropout_date, dropout_reason, replacement_candidate_id, refund_amount, refund_reason } = dropoutForm;

    if (resolution === 'replacement' && !replacement_candidate_id) {
      alert('Please choose the replacement candidate.');
      return;
    }

    setSubmittingLifecycle(true);
    try {
      if (resolution === 'replacement') {
        await axios.patch(`${API}/job-sheets/${id}/replace`,
          { replacement_candidate_id, dropout_date, dropout_reason }, { headers });
      } else if (resolution === 'refund') {
        await axios.patch(`${API}/job-sheets/${id}/refund`,
          { refund_amount, refund_reason, dropout_date, dropout_reason }, { headers });
      } else {
        await axios.patch(`${API}/job-sheets/${id}/dropout`,
          { dropout_date, dropout_reason }, { headers });
      }
      setDropoutModal({ open: false, sheet: null });
      fetchSheets();
    } catch { alert('Failed to record drop-out.'); }
    finally { setSubmittingLifecycle(false); }
  }

  async function markComplete(sheet) {
    if (!window.confirm(`Mark this placement for ${sheet.candidates?.name} as completed? The guarantee period is treated as passed.`)) return;
    try {
      await axios.patch(`${API}/job-sheets/${sheet.id}/complete`, {}, { headers });
      fetchSheets();
    } catch { alert('Failed to complete placement.'); }
  }

  async function sendProfile(sheet) {
    try {
      await axios.patch(`${API}/job-sheets/${sheet.id}/send-profile`, {}, { headers });
      fetchSheets();
    } catch {
      alert('Failed to send profile.');
    }
  }

  async function toggleArchive(sheet) {
    try {
      await axios.patch(`${API}/job-sheets/${sheet.id}/archive`, { archived: !sheet.archived }, { headers });
      fetchSheets();
    } catch {
      alert('Failed to update archive status.');
    }
  }

  async function deleteSheet(sheet) {
    if (!window.confirm(`Delete this job sheet for ${sheet.candidates?.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/job-sheets/${sheet.id}`, { headers });
      fetchSheets();
    } catch {
      alert('Failed to delete job sheet.');
    }
  }

  const filteredCandidates = allCandidates.filter(c =>
    c.name.toLowerCase().includes(candidateSearch.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(candidateSearch.toLowerCase())
  );

  const filteredJobs = allJobs.filter(j =>
    j.title.toLowerCase().includes(jobSearch.toLowerCase()) ||
    (j.clients?.company_name || '').toLowerCase().includes(jobSearch.toLowerCase())
  );

  const sheetsInColumn = (col) => sheets.filter(s => col.statuses.includes(s.status));

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs font-medium text-carriera-muted uppercase tracking-widest mb-1">Recruitment</p>
          <h2 className="font-serif text-3xl font-normal text-carriera-text">Sent Profiles & Interviews</h2>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-carriera-sec cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)}
              className="rounded border-carriera-border" />
            Show archived
          </label>
          <button onClick={openCreateModal}
            className="bg-carriera-text text-white text-sm px-4 py-2 hover:bg-carriera-accent transition-colors duration-200">
            + New Job Sheet
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      {loading ? (
        <p className="text-carriera-muted text-sm">Loading...</p>
      ) : (
        <div className="grid grid-cols-4 gap-4" style={{ minHeight: '60vh' }}>
          {COLUMNS.map(col => {
            const colSheets = sheetsInColumn(col);
            return (
              <div key={col.key} className="flex flex-col">
                {/* Column header */}
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-2 h-2 rounded-full ${col.dot}`} />
                  <span className={`text-xs font-semibold uppercase tracking-wider ${col.color}`}>{col.label}</span>
                  <span className="ml-auto text-xs text-carriera-muted bg-carriera-bg-alt px-2 py-0.5 rounded-full">
                    {colSheets.length}
                  </span>
                </div>

                {/* Cards */}
                <div className="flex flex-col gap-3">
                  {colSheets.length === 0 && (
                    <div className="border border-dashed border-carriera-border rounded p-4 text-center">
                      <p className="text-xs text-carriera-muted">No job sheets</p>
                    </div>
                  )}
                  {colSheets.map(sheet => (
                    <SheetCard
                      key={sheet.id}
                      sheet={sheet}
                      onSendProfile={() => sendProfile(sheet)}
                      onSchedule={() => openInterviewModal(sheet, 'schedule')}
                      onNextRound={() => openInterviewModal(sheet, 'next_round')}
                      onReschedule={() => openInterviewModal(sheet, 'reschedule')}
                      onCancelInterview={() => openCancelModal(sheet)}
                      onRecordOutcome={() => openRoundModal(sheet)}
                      onPlace={() => openOutcomeModal(sheet, 'place')}
                      onReject={() => openOutcomeModal(sheet, 'reject')}
                      onChangePosition={() => openPositionModal(sheet)}
                      onConfirmStarted={() => openStartedModal(sheet)}
                      onRecordInvoice={() => openInvoiceModal(sheet)}
                      onMarkPaid={() => openPaymentModal(sheet)}
                      onMarkComplete={() => markComplete(sheet)}
                      onDropout={() => openDropoutModal(sheet)}
                      onEditPlacement={() => openEditPlacement(sheet)}
                      onToggleArchive={() => toggleArchive(sheet)}
                      onDelete={() => deleteSheet(sheet)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── CREATE JOB SHEET MODAL ── */}
      {showCreate && (
        <Modal title="New Job Sheet"
          onClose={() => confirmClose({ candidate: selectedCandidate, job: selectedJob }, () => setShowCreate(false))}
          width="max-w-lg">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            {[1, 2].map(s => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold
                  ${createStep >= s ? 'bg-carriera-text text-white' : 'bg-carriera-bg border border-carriera-border text-carriera-muted'}`}>
                  {s}
                </div>
                {s === 1 && <span className="text-xs text-carriera-muted">Select Candidate</span>}
                {s === 2 && <span className="text-xs text-carriera-muted">Select Job</span>}
                {s < 2 && <div className="flex-1 border-t border-carriera-border mx-2" style={{ minWidth: 20 }} />}
              </div>
            ))}
          </div>

          {createStep === 1 && (
            <>
              <input
                className="w-full border border-carriera-border px-3 py-2 text-sm mb-3 focus:outline-none focus:border-carriera-accent"
                placeholder="Search candidate by name or email..."
                value={candidateSearch}
                onChange={e => setCandidateSearch(e.target.value)}
                autoFocus
              />
              <div className="border border-carriera-border overflow-y-auto" style={{ maxHeight: 280 }}>
                {filteredCandidates.length === 0
                  ? <p className="text-sm text-carriera-muted p-4 text-center">No candidates found.</p>
                  : filteredCandidates.map(c => (
                    <button key={c.id} onClick={() => { setSelectedCandidate(c); setCreateStep(2); }}
                      className={`w-full text-left px-4 py-3 border-b border-carriera-border text-sm hover:bg-carriera-bg transition-colors
                        ${selectedCandidate?.id === c.id ? 'bg-carriera-bg' : ''}`}>
                      <p className="font-medium text-carriera-text">{c.name}</p>
                      <p className="text-xs text-carriera-muted">{c.email} {c.industry ? `· ${c.industry}` : ''}</p>
                    </button>
                  ))
                }
              </div>
            </>
          )}

          {createStep === 2 && (
            <>
              {selectedCandidate && (
                <div className="bg-carriera-bg border border-carriera-border px-3 py-2 mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-carriera-text">{selectedCandidate.name}</p>
                    <p className="text-xs text-carriera-muted">{selectedCandidate.email}</p>
                  </div>
                  <button onClick={() => { setSelectedCandidate(null); setCreateStep(1); }}
                    className="text-xs text-carriera-muted hover:text-carriera-text">Change</button>
                </div>
              )}
              <input
                className="w-full border border-carriera-border px-3 py-2 text-sm mb-3 focus:outline-none focus:border-carriera-accent"
                placeholder="Search job by title or client..."
                value={jobSearch}
                onChange={e => setJobSearch(e.target.value)}
                autoFocus
              />
              <div className="border border-carriera-border overflow-y-auto mb-4" style={{ maxHeight: 220 }}>
                {filteredJobs.length === 0
                  ? <p className="text-sm text-carriera-muted p-4 text-center">No jobs found.</p>
                  : filteredJobs.map(j => (
                    <button key={j.id} onClick={() => {
                        setSelectedJob(j);
                        if (selectedSecondaryJob?.id === j.id) setSelectedSecondaryJob(null);
                      }}
                      className={`w-full text-left px-4 py-3 border-b border-carriera-border text-sm hover:bg-carriera-bg transition-colors
                        ${selectedJob?.id === j.id ? 'bg-amber-50 border-l-2 border-l-carriera-accent' : ''}`}>
                      <p className="font-medium text-carriera-text">{j.title}</p>
                      <p className="text-xs text-carriera-muted">{j.clients?.company_name || '—'} · {j.status}</p>
                    </button>
                  ))
                }
              </div>

              {selectedJob && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-carriera-text mb-2">
                    Second position at {selectedJob.clients?.company_name || 'this company'} (optional)
                  </p>
                  {selectedSecondaryJob ? (
                    <div className="border border-carriera-accent bg-amber-50 px-3 py-2 flex items-center justify-between">
                      <p className="text-sm text-carriera-text">{selectedSecondaryJob.title}</p>
                      <button onClick={() => setSelectedSecondaryJob(null)}
                        className="text-xs text-carriera-muted hover:text-carriera-text">Remove</button>
                    </div>
                  ) : (
                    <div className="border border-carriera-border overflow-y-auto" style={{ maxHeight: 140 }}>
                      {allJobs.filter(j => j.id !== selectedJob.id && j.client_id === selectedJob.client_id).length === 0
                        ? <p className="text-xs text-carriera-muted p-3 text-center">No other open jobs for this client.</p>
                        : allJobs.filter(j => j.id !== selectedJob.id && j.client_id === selectedJob.client_id).map(j => (
                          <button key={j.id} onClick={() => setSelectedSecondaryJob(j)}
                            className="w-full text-left px-3 py-2 border-b border-carriera-border text-sm hover:bg-carriera-bg transition-colors">
                            <p className="text-carriera-text">{j.title}</p>
                            <p className="text-xs text-carriera-muted">{j.status}</p>
                          </button>
                        ))
                      }
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <button onClick={() => setCreateStep(1)}
                  className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">
                  Back
                </button>
                <button onClick={handleCreate} disabled={!selectedJob || creating}
                  className="text-sm bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors disabled:opacity-40">
                  {creating ? 'Creating...' : 'Create Job Sheet'}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* ── INTERVIEW MODAL ── */}
      {interviewModal.open && (
        <Modal
          title={interviewModal.mode === 'reschedule' ? 'Reschedule Interview'
            : interviewModal.mode === 'next_round' ? `Schedule Round ${currentRoundNumber(interviewModal.sheet) + 1}`
            : 'Schedule Interview'}
          onClose={() => confirmClose(interviewForm, () => setInterviewModal({ open: false, sheet: null, mode: 'schedule' }))}
          width="max-w-lg">
          <div className="mb-3">
            <p className="text-xs text-carriera-muted mb-1">Candidate</p>
            <p className="text-sm font-medium text-carriera-text">{interviewModal.sheet?.candidates?.name}</p>
            <p className="text-xs text-carriera-muted">
              {interviewModal.sheet?.jobs?.title}{interviewModal.sheet?.secondary_job ? ` + ${interviewModal.sheet.secondary_job.title}` : ''} · {interviewModal.sheet?.jobs?.clients?.company_name}
            </p>
            {interviewModal.mode === 'next_round' && (
              <p className="text-xs text-amber-600 mt-1">Previous rounds are kept. This adds Round {currentRoundNumber(interviewModal.sheet) + 1}.</p>
            )}
          </div>
          <div className="border-t border-carriera-border pt-4">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-carriera-muted mb-1">Date *</label>
                <input type="date" value={interviewForm.interview_date}
                  onChange={e => setInterviewForm(f => ({ ...f, interview_date: e.target.value }))}
                  className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
              </div>
              <div>
                <label className="block text-xs text-carriera-muted mb-1">Time *</label>
                <input type="time" value={interviewForm.interview_time}
                  onChange={e => setInterviewForm(f => ({ ...f, interview_time: e.target.value }))}
                  className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs text-carriera-muted mb-1">Type *</label>
              <div className="flex gap-4">
                {['face_to_face', 'online'].map(t => (
                  <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" value={t} checked={interviewForm.interview_type === t}
                      onChange={e => setInterviewForm(f => ({ ...f, interview_type: e.target.value }))} />
                    {t === 'face_to_face' ? 'Face-to-face' : 'Online'}
                  </label>
                ))}
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs text-carriera-muted mb-1">
                {interviewForm.interview_type === 'online' ? 'Meeting Link' : 'Location'}
              </label>
              {clientLocations.length > 0 && interviewForm.interview_type === 'face_to_face' && (
                <select className="w-full border border-carriera-border px-3 py-2 text-sm mb-2 focus:outline-none focus:border-carriera-accent"
                  onChange={e => setInterviewForm(f => ({ ...f, location_or_link: e.target.value }))}>
                  <option value="">Select a location...</option>
                  {clientLocations.map(loc => (
                    <option key={loc.id} value={loc.address}>{loc.name} — {loc.address}</option>
                  ))}
                </select>
              )}
              <input type="text" value={interviewForm.location_or_link}
                onChange={e => setInterviewForm(f => ({ ...f, location_or_link: e.target.value }))}
                placeholder={interviewForm.interview_type === 'online' ? 'https://zoom.us/...' : 'Address'}
                className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-carriera-muted mb-1">Contact Person</label>
                <input type="text" value={interviewForm.contact_person}
                  onChange={e => setInterviewForm(f => ({ ...f, contact_person: e.target.value }))}
                  className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
              </div>
              <div>
                <label className="block text-xs text-carriera-muted mb-1">Department</label>
                <input type="text" value={interviewForm.contact_department}
                  onChange={e => setInterviewForm(f => ({ ...f, contact_department: e.target.value }))}
                  className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs text-carriera-muted mb-1">Documents to bring (one per line)</label>
              <textarea rows={2} value={interviewForm.documents}
                onChange={e => setInterviewForm(f => ({ ...f, documents: e.target.value }))}
                className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent resize-none" />
            </div>
            <div className="mb-4">
              <label className="block text-xs text-carriera-muted mb-1">Notes</label>
              <textarea rows={2} value={interviewForm.interview_notes}
                onChange={e => setInterviewForm(f => ({ ...f, interview_notes: e.target.value }))}
                className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent resize-none" />
            </div>
            <label className="flex items-center gap-2 text-sm text-carriera-sec mb-4 cursor-pointer">
              <input type="checkbox" checked={interviewForm.send_email}
                onChange={e => setInterviewForm(f => ({ ...f, send_email: e.target.checked }))} />
              Send confirmation email to candidate
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={() => confirmClose(interviewForm, () => setInterviewModal({ open: false, sheet: null, mode: 'schedule' }))}
                className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">
                Cancel
              </button>
              <button onClick={submitInterview} disabled={submittingInterview}
                className="text-sm bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors disabled:opacity-40">
                {submittingInterview ? 'Saving...'
                  : interviewModal.mode === 'reschedule' ? 'Reschedule'
                  : interviewModal.mode === 'next_round' ? 'Schedule Next Round'
                  : 'Schedule Interview'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── ROUND OUTCOME MODAL (silent) ── */}
      {roundModal.open && (
        <Modal title={`Record Round ${roundModal.round?.round_number} Outcome`}
          onClose={() => setRoundModal({ open: false, sheet: null, round: null })} width="max-w-md">
          <p className="text-sm text-carriera-text mb-1">
            <span className="font-medium">{roundModal.sheet?.candidates?.name}</span>
            {' — '}{roundModal.sheet?.jobs?.clients?.company_name}
          </p>
          <p className="text-xs text-carriera-muted mb-4">This is recorded internally. No email is sent to the candidate.</p>
          <div className="mb-3">
            <label className="block text-xs text-carriera-muted mb-1">Outcome</label>
            <div className="flex gap-4">
              {[['passed', 'Passed'], ['failed', 'Failed']].map(([val, lbl]) => (
                <label key={val} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" value={val} checked={roundOutcome === val}
                    onChange={e => setRoundOutcome(e.target.value)} />
                  {lbl}
                </label>
              ))}
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-xs text-carriera-muted mb-1">Feedback (optional)</label>
            <textarea rows={3} value={roundFeedback} onChange={e => setRoundFeedback(e.target.value)}
              placeholder="e.g. Strong technically, client wants a 2nd round with the director..."
              className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent resize-none" />
          </div>
          {roundOutcome === 'passed' && (
            <p className="text-xs text-carriera-muted mb-4">Tip: after saving, use “Next Round” to schedule the following interview, or “Mark Placed”.</p>
          )}
          {roundOutcome === 'failed' && (
            <p className="text-xs text-carriera-muted mb-4">Tip: after saving, use “Mark Rejected” if the client will not proceed.</p>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={() => setRoundModal({ open: false, sheet: null, round: null })}
              className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">
              Cancel
            </button>
            <button onClick={submitRoundOutcome} disabled={submittingRound}
              className="text-sm bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors disabled:opacity-40">
              {submittingRound ? 'Saving...' : 'Save Outcome'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── CANCEL INTERVIEW MODAL ── */}
      {cancelModal.open && (
        <Modal title="Cancel Interview" onClose={() => confirmClose({ sendEmail: cancelSendEmail, reason: cancelReason }, () => setCancelModal({ open: false, sheet: null }))} width="max-w-md">
          <p className="text-sm text-carriera-sec mb-4">
            Cancel the current interview round for <span className="font-medium text-carriera-text">{cancelModal.sheet?.candidates?.name}</span>?
            If no other round remains scheduled, the card moves back to Profile Sent.
          </p>
          <div className="mb-4">
            <label className="block text-xs text-carriera-muted mb-1">Reason for cancellation (optional)</label>
            <textarea rows={2} value={cancelReason} onChange={e => setCancelReason(e.target.value)}
              placeholder="e.g. Client postponed, candidate withdrew..."
              className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent resize-none" />
          </div>
          <label className="flex items-center gap-2 text-sm text-carriera-sec mb-6 cursor-pointer">
            <input type="checkbox" checked={cancelSendEmail} onChange={e => setCancelSendEmail(e.target.checked)} />
            Send cancellation email to candidate
          </label>
          <div className="flex gap-2 justify-end">
            <button onClick={() => confirmClose({ sendEmail: cancelSendEmail, reason: cancelReason }, () => setCancelModal({ open: false, sheet: null }))}
              className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">
              Back
            </button>
            <button onClick={submitCancel} disabled={submittingCancel}
              className="text-sm bg-red-500 text-white px-4 py-2 hover:bg-red-600 transition-colors disabled:opacity-40">
              {submittingCancel ? 'Cancelling...' : 'Confirm Cancel'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── OUTCOME MODAL (place / reject) ── */}
      {outcomeModal.open && (
        <Modal
          title={outcomeModal.type === 'place' ? 'Mark as Placed' : outcomeModal.type === 'edit' ? 'Edit Placement Details' : 'Mark as Rejected'}
          onClose={() => confirmClose({ notes: outcomeNotes, startDate, offerPackage }, () => setOutcomeModal({ open: false, sheet: null, type: '' }))}
          width="max-w-md">
          <div className="mb-3">
            <p className="text-sm text-carriera-text">
              <span className="font-medium">{outcomeModal.sheet?.candidates?.name}</span>
              {' — '}{outcomeModal.sheet?.jobs?.title}{outcomeModal.sheet?.secondary_job ? ` + ${outcomeModal.sheet.secondary_job.title}` : ''}
            </p>
            <p className="text-xs text-carriera-muted">{outcomeModal.sheet?.jobs?.clients?.company_name}</p>
          </div>

          {/* Placed in which position? (resolves a + b, or a different role) */}
          {(outcomeModal.type === 'place' || outcomeModal.type === 'edit') && (() => {
            const sheet = outcomeModal.sheet;
            const primaryTitle = sheet?.jobs?.title;
            const secondaryTitle = sheet?.secondary_job?.title;
            const clientId = sheet?.jobs?.client_id;
            const otherJobs = allJobs.filter(
              j => j.client_id === clientId && j.id !== sheet?.job_id && j.id !== sheet?.secondary_job_id
            );
            return (
              <div className="border border-carriera-border bg-carriera-bg px-3 py-3 mb-4">
                <p className="text-xs font-medium text-carriera-text mb-2">Placed in which position?</p>
                <div className="flex flex-col gap-1.5">
                  {outcomeModal.type === 'edit' && (
                    <label className="flex items-center gap-2 text-sm text-carriera-sec cursor-pointer">
                      <input type="radio" value="keep" checked={placedChoice === 'keep'}
                        onChange={e => setPlacedChoice(e.target.value)} />
                      Keep current{secondaryTitle ? ` (${primaryTitle} + ${secondaryTitle})` : ''}
                    </label>
                  )}
                  <label className="flex items-center gap-2 text-sm text-carriera-sec cursor-pointer">
                    <input type="radio" value="primary" checked={placedChoice === 'primary'}
                      onChange={e => setPlacedChoice(e.target.value)} />
                    {primaryTitle || 'Primary position'}
                  </label>
                  {secondaryTitle && (
                    <>
                      <label className="flex items-center gap-2 text-sm text-carriera-sec cursor-pointer">
                        <input type="radio" value="secondary" checked={placedChoice === 'secondary'}
                          onChange={e => setPlacedChoice(e.target.value)} />
                        {secondaryTitle}
                      </label>
                      <label className="flex items-center gap-2 text-sm text-carriera-sec cursor-pointer">
                        <input type="radio" value="both" checked={placedChoice === 'both'}
                          onChange={e => setPlacedChoice(e.target.value)} />
                        Both positions
                      </label>
                    </>
                  )}
                  <label className="flex items-center gap-2 text-sm text-carriera-sec cursor-pointer">
                    <input type="radio" value="other" checked={placedChoice === 'other'}
                      onChange={e => setPlacedChoice(e.target.value)} />
                    A different position…
                  </label>
                </div>

                {placedChoice === 'other' && (
                  <div className="mt-2 pl-6">
                    <div className="flex gap-4 mb-2">
                      {[['existing', 'Existing role'], ['new', 'Add new']].map(([val, lbl]) => (
                        <label key={val} className="flex items-center gap-2 text-xs text-carriera-sec cursor-pointer">
                          <input type="radio" value={val} checked={placedOtherMode === val}
                            onChange={e => setPlacedOtherMode(e.target.value)} />
                          {lbl}
                        </label>
                      ))}
                    </div>
                    {placedOtherMode === 'existing' ? (
                      otherJobs.length === 0 ? (
                        <p className="text-xs text-carriera-muted">No other roles for this employer. Use “Add new”.</p>
                      ) : (
                        <select value={placedOtherJobId} onChange={e => setPlacedOtherJobId(e.target.value)}
                          className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent">
                          <option value="">Select a role...</option>
                          {otherJobs.map(j => (
                            <option key={j.id} value={j.id}>{j.title}{j.status !== 'live' ? ` (${j.status})` : ''}</option>
                          ))}
                        </select>
                      )
                    ) : (
                      <>
                        <input type="text" value={placedOtherTitle} onChange={e => setPlacedOtherTitle(e.target.value)}
                          placeholder="e.g. Assistant Manager"
                          className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
                        <p className="text-xs text-carriera-muted mt-1">Created as a draft under this employer (not shown on the website).</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Client terms reference + which company actually hired */}
          {(outcomeModal.type === 'place' || outcomeModal.type === 'edit') && (
            <div className="mb-4">
              {placeClient?.placement_terms && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 mb-3">
                  <span className="font-medium">Client terms:</span> {placeClient.placement_terms}
                </div>
              )}
              <p className="text-xs font-medium text-carriera-text mb-1">Hiring company (employer on JTK / invoice)</p>
              <select value={hiringChoice} onChange={e => setHiringChoice(e.target.value)}
                className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent">
                <option value="client">{outcomeModal.sheet?.jobs?.clients?.company_name || 'The client itself'}</option>
                {(placeClient?.subsidiaries || []).map((s, i) => (
                  <option key={i} value={String(i)}>{s.name}</option>
                ))}
                <option value="new">+ Add a new subsidiary…</option>
              </select>
              {hiringChoice === 'new' && (
                <div className="mt-2 space-y-2">
                  <input type="text" value={newSub.name} onChange={e => setNewSub(s => ({ ...s, name: e.target.value }))}
                    placeholder="Subsidiary name"
                    className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={newSub.ssm_number} onChange={e => setNewSub(s => ({ ...s, ssm_number: e.target.value }))}
                      placeholder="SSM no."
                      className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
                    <input type="text" value={newSub.address} onChange={e => setNewSub(s => ({ ...s, address: e.target.value }))}
                      placeholder="Address"
                      className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
                  </div>
                  <p className="text-xs text-carriera-muted">Saved to this client so you can pick it next time.</p>
                </div>
              )}
            </div>
          )}

          {(outcomeModal.type === 'place' || outcomeModal.type === 'edit') && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-carriera-muted mb-1">Start date</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
                </div>
                <div>
                  <label className="block text-xs text-carriera-muted mb-1">Offer letter date</label>
                  <input type="date" value={placeForm.offer_letter_date}
                    onChange={e => setPlaceForm(f => ({ ...f, offer_letter_date: e.target.value }))}
                    className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-carriera-muted mb-1">Guarantee period</label>
                  <div className="flex gap-2">
                    <input type="number" min="0" value={guaranteeMonths} onChange={e => setGuaranteeMonths(e.target.value)}
                      placeholder="e.g. 3"
                      className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
                    <select value={guaranteeUnit} onChange={e => setGuaranteeUnit(e.target.value)}
                      className="border border-carriera-border px-2 py-2 text-sm focus:outline-none focus:border-carriera-accent">
                      <option value="months">months</option>
                      <option value="days">days</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-carriera-muted mb-1">Monthly salary (RM) · Upah/Gaji</label>
                  <input type="number" min="0" step="0.01" value={placeForm.monthly_salary}
                    onChange={e => setPlaceForm(f => ({ ...f, monthly_salary: e.target.value }))}
                    placeholder="e.g. 3500.00"
                    className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
                </div>
              </div>

              {/* Billing the client — always a flat amount, keyed in manually */}
              <div className="mb-3">
                <label className="block text-xs text-carriera-muted mb-1">Placement fee (RM)</label>
                <input type="number" min="0" step="0.01" value={placeForm.fee_amount}
                  onChange={e => setPlaceForm(f => ({ ...f, fee_amount: e.target.value }))}
                  placeholder="Flat amount, e.g. 5500"
                  className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
              </div>

              {/* Jobseeker details for the JTK annual report */}
              <div className="border-t border-carriera-border pt-3 mb-3">
                <p className="text-xs font-medium text-carriera-muted mb-2">Candidate details (for JTK report)</p>
                <div className="mb-3">
                  <label className="block text-xs text-carriera-muted mb-1">IC number · No. K/P</label>
                  <input type="text" value={placeForm.ic_number}
                    onChange={e => setPlaceForm(f => ({ ...f, ic_number: e.target.value }))}
                    placeholder="e.g. 900101-14-5678"
                    className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
                </div>
                <div>
                  <label className="block text-xs text-carriera-muted mb-1">Full address · Alamat</label>
                  <textarea rows={2} value={placeForm.candidate_address}
                    onChange={e => setPlaceForm(f => ({ ...f, candidate_address: e.target.value }))}
                    placeholder="Candidate's full home address"
                    className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent resize-none" />
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-xs text-carriera-muted mb-1">Offer / Package note (optional)</label>
                <input type="text" value={offerPackage} onChange={e => setOfferPackage(e.target.value)}
                  placeholder="e.g. RM3,500/mo + allowances + EPF"
                  className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
              </div>
              {outcomeModal.type === 'place' && (
                <p className="text-xs text-carriera-muted mb-3">
                  A start-work reminder (9-10am) will be added to Steph's calendar and emailed to you on the start date.
                  The guarantee period begins on the confirmed start date; the invoice is raised on the start day, due 14 days later.
                </p>
              )}
            </>
          )}
          {outcomeModal.type !== 'edit' && (
            <>
              <div className="mb-4">
                <label className="block text-xs text-carriera-muted mb-1">
                  {outcomeModal.type === 'place' ? 'Notes (optional)' : 'Reason / Notes (optional)'}
                </label>
                <textarea rows={3} value={outcomeNotes} onChange={e => setOutcomeNotes(e.target.value)}
                  placeholder={outcomeModal.type === 'place' ? 'Any other details...' : 'e.g. Client chose another candidate, salary mismatch...'}
                  className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent resize-none" />
              </div>
              <label className="flex items-center gap-2 text-sm text-carriera-sec mb-4 cursor-pointer">
                <input type="checkbox" checked={outcomeSendEmail} onChange={e => setOutcomeSendEmail(e.target.checked)} />
                Send notification email to candidate
              </label>
            </>
          )}
          {outcomeModal.type === 'reject' && (
            <p className="text-xs text-carriera-muted mb-4">This job sheet will be automatically archived.</p>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={() => confirmClose({ notes: outcomeNotes, startDate, offerPackage }, () => setOutcomeModal({ open: false, sheet: null, type: '' }))}
              className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">
              Cancel
            </button>
            <button onClick={submitOutcome} disabled={submittingOutcome}
              className={`text-sm text-white px-4 py-2 transition-colors disabled:opacity-40
                ${outcomeModal.type === 'reject' ? 'bg-red-500 hover:bg-red-600' : outcomeModal.type === 'edit' ? 'bg-carriera-text hover:bg-carriera-accent' : 'bg-green-600 hover:bg-green-700'}`}>
              {submittingOutcome ? 'Saving...' : (outcomeModal.type === 'place' ? 'Confirm Placed' : outcomeModal.type === 'edit' ? 'Save Details' : 'Confirm Rejected')}
            </button>
          </div>
        </Modal>
      )}

      {/* ── CONFIRM STARTED MODAL ── */}
      {startedModal.open && (
        <Modal title="Confirm Candidate Started" onClose={() => setStartedModal({ open: false, sheet: null })} width="max-w-md">
          <p className="text-sm text-carriera-text mb-1">
            <span className="font-medium">{startedModal.sheet?.candidates?.name}</span> — {startedModal.sheet?.jobs?.clients?.company_name}
          </p>
          <p className="text-xs text-carriera-muted mb-4">
            Confirm the candidate has started work. This starts the guarantee period and you can then bill the client.
          </p>
          <div className="mb-4">
            <label className="block text-xs text-carriera-muted mb-1">Actual start date</label>
            <input type="date" value={actualStartDate} onChange={e => setActualStartDate(e.target.value)}
              className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setStartedModal({ open: false, sheet: null })}
              className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">Cancel</button>
            <button onClick={submitStarted} disabled={submittingLifecycle}
              className="text-sm bg-green-600 text-white px-4 py-2 hover:bg-green-700 transition-colors disabled:opacity-40">
              {submittingLifecycle ? 'Saving...' : 'Confirm Started'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── INVOICE MODAL ── */}
      {invoiceModal.open && (
        <Modal title="Record Invoice" onClose={() => setInvoiceModal({ open: false, sheet: null })} width="max-w-md">
          <p className="text-sm text-carriera-text mb-1">
            <span className="font-medium">{invoiceModal.sheet?.candidates?.name}</span> — {invoiceModal.sheet?.jobs?.clients?.company_name}
          </p>
          <p className="text-xs text-carriera-muted mb-4">Bill the client for this placement.</p>
          <div className="mb-3">
            <label className="block text-xs text-carriera-muted mb-1">Fee basis</label>
            <div className="flex gap-4">
              {[['percent', '% of salary'], ['flat', 'Flat amount']].map(([val, lbl]) => (
                <label key={val} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" value={val} checked={invoiceForm.fee_basis === val}
                    onChange={e => setInvoiceForm(f => ({ ...f, fee_basis: e.target.value }))} />
                  {lbl}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            {invoiceForm.fee_basis === 'percent' && (
              <div>
                <label className="block text-xs text-carriera-muted mb-1">Fee %</label>
                <input type="number" min="0" value={invoiceForm.fee_percent}
                  onChange={e => setInvoiceForm(f => ({ ...f, fee_percent: e.target.value }))}
                  placeholder="e.g. 8.33"
                  className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
              </div>
            )}
            <div>
              <label className="block text-xs text-carriera-muted mb-1">Fee amount (RM)</label>
              <input type="number" min="0" value={invoiceForm.fee_amount}
                onChange={e => setInvoiceForm(f => ({ ...f, fee_amount: e.target.value }))}
                placeholder="e.g. 5500"
                className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs text-carriera-muted mb-1">Invoice number</label>
              <input type="text" value={invoiceForm.invoice_number}
                onChange={e => setInvoiceForm(f => ({ ...f, invoice_number: e.target.value }))}
                placeholder="e.g. INV-2026-014"
                className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
            </div>
            <div>
              <label className="block text-xs text-carriera-muted mb-1">Invoice sent date</label>
              <input type="date" value={invoiceForm.invoice_sent_date}
                onChange={e => setInvoiceForm(f => ({ ...f, invoice_sent_date: e.target.value }))}
                className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setInvoiceModal({ open: false, sheet: null })}
              className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">Cancel</button>
            <button onClick={submitInvoice} disabled={submittingLifecycle}
              className="text-sm bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors disabled:opacity-40">
              {submittingLifecycle ? 'Saving...' : 'Record Invoice'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── PAYMENT MODAL ── */}
      {paymentModal.open && (
        <Modal title="Mark Invoice Paid" onClose={() => setPaymentModal({ open: false, sheet: null })} width="max-w-md">
          <p className="text-sm text-carriera-text mb-1">
            <span className="font-medium">{paymentModal.sheet?.candidates?.name}</span> — {paymentModal.sheet?.jobs?.clients?.company_name}
          </p>
          <p className="text-xs text-carriera-muted mb-4">
            {paymentModal.sheet?.invoice_number ? `Invoice ${paymentModal.sheet.invoice_number}. ` : ''}Record the date payment was received.
          </p>
          <div className="mb-4">
            <label className="block text-xs text-carriera-muted mb-1">Paid date</label>
            <input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)}
              className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setPaymentModal({ open: false, sheet: null })}
              className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">Cancel</button>
            <button onClick={submitPayment} disabled={submittingLifecycle}
              className="text-sm bg-green-600 text-white px-4 py-2 hover:bg-green-700 transition-colors disabled:opacity-40">
              {submittingLifecycle ? 'Saving...' : 'Mark Paid'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── DROPOUT MODAL ── */}
      {dropoutModal.open && (
        <Modal title="Record Drop-out" onClose={() => setDropoutModal({ open: false, sheet: null })} width="max-w-md">
          <p className="text-sm text-carriera-text mb-1">
            <span className="font-medium">{dropoutModal.sheet?.candidates?.name}</span> — {dropoutModal.sheet?.jobs?.clients?.company_name}
          </p>
          <p className="text-xs text-carriera-muted mb-4">
            The candidate left during the guarantee period. A replacement or refund may be due to the client.
          </p>
          <div className="mb-3">
            <label className="block text-xs text-carriera-muted mb-1">Drop-out date</label>
            <input type="date" value={dropoutForm.dropout_date}
              onChange={e => setDropoutForm(f => ({ ...f, dropout_date: e.target.value }))}
              className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
          </div>
          <div className="mb-3">
            <label className="block text-xs text-carriera-muted mb-1">Reason</label>
            <textarea rows={2} value={dropoutForm.dropout_reason}
              onChange={e => setDropoutForm(f => ({ ...f, dropout_reason: e.target.value }))}
              placeholder="e.g. Candidate resigned, counter-offer from previous employer..."
              className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent resize-none" />
          </div>

          {/* How is this resolved? */}
          <div className="mb-3">
            <label className="block text-xs text-carriera-muted mb-1">Resolution</label>
            <div className="flex flex-col gap-1.5">
              {[
                ['replacement', 'Provide a free replacement (no additional charge)'],
                ['refund', 'Issue a refund (rare)'],
                ['none', 'None yet / record drop-out only']
              ].map(([val, lbl]) => (
                <label key={val} className="flex items-center gap-2 text-sm text-carriera-sec cursor-pointer">
                  <input type="radio" value={val} checked={dropoutForm.resolution === val}
                    onChange={e => setDropoutForm(f => ({ ...f, resolution: e.target.value }))} />
                  {lbl}
                </label>
              ))}
            </div>
          </div>

          {dropoutForm.resolution === 'replacement' && (
            <div className="mb-4">
              <label className="block text-xs text-carriera-muted mb-1">Replacement candidate</label>
              <select value={dropoutForm.replacement_candidate_id}
                onChange={e => setDropoutForm(f => ({ ...f, replacement_candidate_id: e.target.value }))}
                className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent">
                <option value="">Select candidate...</option>
                {allCandidates
                  .filter(c => c.id !== dropoutModal.sheet?.candidate_id)
                  .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <p className="text-xs text-carriera-muted mt-1">
                A new placed job sheet is created for them on the same role, with no new charge.
              </p>
            </div>
          )}

          {dropoutForm.resolution === 'refund' && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs text-carriera-muted mb-1">Refund amount (RM)</label>
                <input type="number" min="0" value={dropoutForm.refund_amount}
                  onChange={e => setDropoutForm(f => ({ ...f, refund_amount: e.target.value }))}
                  placeholder="e.g. 5500"
                  className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
              </div>
              <div>
                <label className="block text-xs text-carriera-muted mb-1">Refund note (optional)</label>
                <input type="text" value={dropoutForm.refund_reason}
                  onChange={e => setDropoutForm(f => ({ ...f, refund_reason: e.target.value }))}
                  placeholder="e.g. Partial, full..."
                  className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button onClick={() => setDropoutModal({ open: false, sheet: null })}
              className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">Cancel</button>
            <button onClick={submitDropout} disabled={submittingLifecycle}
              className="text-sm bg-red-500 text-white px-4 py-2 hover:bg-red-600 transition-colors disabled:opacity-40">
              {submittingLifecycle ? 'Saving...'
                : dropoutForm.resolution === 'replacement' ? 'Drop-out + Replacement'
                : dropoutForm.resolution === 'refund' ? 'Drop-out + Refund'
                : 'Record Drop-out'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── CHANGE POSITION MODAL ── */}
      {positionModal.open && (() => {
        const clientId = positionModal.sheet?.jobs?.client_id;
        const clientName = positionModal.sheet?.jobs?.clients?.company_name || 'this employer';
        const sameEmployerJobs = allJobs.filter(
          j => j.client_id === clientId && j.id !== positionModal.sheet?.job_id
        );
        return (
          <Modal title="Change Position" onClose={() => setPositionModal({ open: false, sheet: null })} width="max-w-md">
            <div className="mb-4">
              <p className="text-sm text-carriera-text">
                <span className="font-medium">{positionModal.sheet?.candidates?.name}</span>
              </p>
              <p className="text-xs text-carriera-muted">
                Currently: {positionModal.sheet?.jobs?.title || '—'} · {clientName}
              </p>
              <p className="text-xs text-carriera-muted mt-1">
                Move this candidate to a different role at <span className="font-medium">{clientName}</span>.
                Interview history stays on this card.
              </p>
            </div>

            <div className="flex flex-col gap-1.5 mb-3">
              {[
                ['existing', 'Pick an existing role at this employer'],
                ['new', 'Add a new position (not listed yet)']
              ].map(([val, lbl]) => (
                <label key={val} className="flex items-center gap-2 text-sm text-carriera-sec cursor-pointer">
                  <input type="radio" value={val} checked={positionMode === val}
                    onChange={e => setPositionMode(e.target.value)} />
                  {lbl}
                </label>
              ))}
            </div>

            {positionMode === 'existing' ? (
              <div className="mb-4">
                {sameEmployerJobs.length === 0 ? (
                  <p className="text-xs text-carriera-muted border border-dashed border-carriera-border p-3">
                    No other roles for this employer. Choose “Add a new position” above.
                  </p>
                ) : (
                  <select value={positionJobId} onChange={e => setPositionJobId(e.target.value)}
                    className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent">
                    <option value="">Select a role...</option>
                    {sameEmployerJobs.map(j => (
                      <option key={j.id} value={j.id}>{j.title}{j.status !== 'live' ? ` (${j.status})` : ''}</option>
                    ))}
                  </select>
                )}
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-xs text-carriera-muted mb-1">New position title</label>
                <input type="text" value={positionNewTitle} onChange={e => setPositionNewTitle(e.target.value)}
                  placeholder="e.g. Senior Sales Executive" autoFocus
                  className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent" />
                <p className="text-xs text-carriera-muted mt-1">
                  Created as a draft under {clientName} (not shown on the website). You can flesh it out later in Jobs.
                </p>
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button onClick={() => setPositionModal({ open: false, sheet: null })}
                className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">Cancel</button>
              <button onClick={submitPosition} disabled={submittingPosition}
                className="text-sm bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors disabled:opacity-40">
                {submittingPosition ? 'Saving...' : 'Change Position'}
              </button>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

// ── Sheet Card ────────────────────────────────────
function SheetCard({
  sheet, onSendProfile, onSchedule, onNextRound, onReschedule, onCancelInterview, onRecordOutcome,
  onPlace, onReject, onChangePosition, onConfirmStarted, onRecordInvoice, onMarkPaid, onMarkComplete, onDropout,
  onEditPlacement, onToggleArchive, onDelete
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const candidate = sheet.candidates;
  const job = sheet.jobs;
  const client = job?.clients;

  const isPlacementStage = PLACEMENT_STAGE.includes(sheet.status);
  const isPostPlacement = COLUMNS[3].statuses.includes(sheet.status);
  const badge = isPostPlacement ? guaranteeBadge(sheet) : null;
  const badgeTone = {
    red: 'bg-red-50 text-red-600 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200'
  };

  return (
    <div className={`bg-white border border-carriera-border p-4 ${sheet.archived ? 'opacity-60' : ''}`}
      style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>

      {sheet.archived && (
        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded mb-2 inline-block">Archived</span>
      )}

      <p className="font-medium text-carriera-text text-sm leading-tight">{candidate?.name || '—'}</p>
      <p className="text-xs text-carriera-muted mt-0.5">
        {job?.title || '—'}{sheet.secondary_job ? ` + ${sheet.secondary_job.title}` : ''}
      </p>
      <p className="text-xs text-carriera-accent font-medium">{client?.company_name || '—'}</p>

      {(candidate?.phone || candidate?.email) && (
        <div className="mt-2 space-y-0.5">
          {candidate.phone && (
            <p className="text-xs text-carriera-sec">{candidate.phone}</p>
          )}
          {candidate.email && (
            <p className="text-xs text-carriera-muted">{candidate.email}</p>
          )}
        </div>
      )}

      <div className="border-t border-carriera-border my-3" />

      {sheet.status === 'interview_scheduled' && sheet.interview_date ? (
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
              Round {currentRoundNumber(sheet)}
            </span>
          </div>
          <p className="text-xs text-carriera-muted">
            Interview: <span className="text-carriera-text font-medium">
              {new Date(sheet.interview_date + 'T00:00:00').toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })}{' '}
              {formatTime(sheet.interview_time)}
            </span>
          </p>
          {sheet.location_or_link && (
            <p className="text-xs text-carriera-muted mt-0.5 truncate" title={sheet.location_or_link}>
              {sheet.interview_type === 'online' ? '🔗' : '📍'} {sheet.location_or_link}
            </p>
          )}
        </div>
      ) : !isPostPlacement ? (
        <p className="text-xs text-carriera-muted mb-3">
          Sent: {new Date(sheet.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
        </p>
      ) : null}

      {/* Post-placement: lifecycle strip + guarantee badge */}
      {isPostPlacement && (
        <div className="mb-3">
          {sheet.status !== 'dropped_out' && (
            <div className="flex items-center gap-1 mb-2">
              {LIFECYCLE.map((stage, i) => {
                const reached = LIFECYCLE.indexOf(sheet.status) >= i;
                return (
                  <div key={stage} className="flex items-center gap-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${reached ? 'bg-green-600 text-white' : 'bg-carriera-bg text-carriera-muted'}`}>
                      {LIFECYCLE_LABELS[stage]}
                    </span>
                    {i < LIFECYCLE.length - 1 && <span className="text-carriera-muted text-[10px]">›</span>}
                  </div>
                );
              })}
            </div>
          )}
          {badge && (
            <p className={`text-xs px-2 py-1 border rounded ${badgeTone[badge.tone]}`}>{badge.text}</p>
          )}
        </div>
      )}

      {(sheet.start_date || sheet.offer_package || sheet.outcome_notes || sheet.fee_amount || sheet.invoice_number || sheet.invoice_due_date || sheet.refund_amount) && (
        <div className="bg-carriera-bg border border-carriera-border px-2.5 py-2 mb-3 space-y-1">
          {sheet.start_date && (
            <p className="text-xs text-carriera-sec">
              <span className="text-carriera-muted">Start:</span> {formatDate(sheet.actual_start_date || sheet.start_date)}
              {sheet.started_confirmed && <span className="text-green-600"> ✓</span>}
            </p>
          )}
          {sheet.offer_package && (
            <p className="text-xs text-carriera-sec">
              <span className="text-carriera-muted">Package:</span> {sheet.offer_package}
            </p>
          )}
          {(sheet.fee_amount || sheet.fee_percent) && (
            <p className="text-xs text-carriera-sec">
              <span className="text-carriera-muted">Fee:</span> {sheet.fee_amount ? `RM${sheet.fee_amount}` : ''}{sheet.fee_percent ? ` (${sheet.fee_percent}%)` : ''}
              {sheet.payment_status === 'paid'
                ? <span className="text-green-600"> · Paid</span>
                : sheet.invoice_number ? <span className="text-amber-600"> · Unpaid</span> : ''}
            </p>
          )}
          {sheet.invoice_number && (
            <p className="text-xs text-carriera-sec"><span className="text-carriera-muted">Invoice:</span> {sheet.invoice_number}</p>
          )}
          {sheet.invoice_due_date && sheet.payment_status !== 'paid' && (
            <p className="text-xs text-carriera-sec">
              <span className="text-carriera-muted">Due:</span> {formatDate(sheet.invoice_due_date)}
              {daysUntil(sheet.invoice_due_date) < 0
                ? <span className="text-red-600"> · Overdue</span>
                : <span className="text-carriera-muted"> · in {daysUntil(sheet.invoice_due_date)} day{daysUntil(sheet.invoice_due_date) === 1 ? '' : 's'}</span>}
            </p>
          )}
          {sheet.dropout_reason && (
            <p className="text-xs text-red-600"><span className="text-carriera-muted">Drop-out:</span> {sheet.dropout_reason}</p>
          )}
          {sheet.refund_amount && (
            <p className="text-xs text-red-600"><span className="text-carriera-muted">Refund:</span> RM{sheet.refund_amount}</p>
          )}
          {sheet.outcome_notes && (
            <div>
              <p className="text-xs text-carriera-muted font-medium mb-0.5">Notes</p>
              <p className="text-xs text-carriera-sec leading-snug">{sheet.outcome_notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {!sheet.archived && (
        <div className="flex items-center gap-2 relative flex-wrap">
          {sheet.status === 'profile_prepared' && (
            <button onClick={onSendProfile}
              className="text-xs bg-carriera-text text-white px-2.5 py-1 hover:bg-carriera-accent transition-colors">
              Send Profile
            </button>
          )}
          {sheet.status === 'profile_sent' && (
            <button onClick={onSchedule}
              className="text-xs bg-carriera-text text-white px-2.5 py-1 hover:bg-carriera-accent transition-colors">
              Schedule Interview
            </button>
          )}
          {sheet.status === 'interview_scheduled' && (
            <>
              <button onClick={onRecordOutcome}
                className="text-xs bg-carriera-text text-white px-2.5 py-1 hover:bg-carriera-accent transition-colors">
                Record Outcome
              </button>
              <button onClick={onNextRound}
                className="text-xs border border-carriera-border text-carriera-sec px-2.5 py-1 hover:border-carriera-accent transition-colors">
                Next Round
              </button>
              <button onClick={onReschedule}
                className="text-xs border border-carriera-border text-carriera-sec px-2.5 py-1 hover:border-carriera-accent transition-colors">
                Reschedule
              </button>
              <button onClick={onCancelInterview}
                className="text-xs border border-carriera-border text-carriera-sec px-2.5 py-1 hover:border-red-400 hover:text-red-500 transition-colors">
                Cancel
              </button>
            </>
          )}
          {sheet.status === 'placed' && (
            <button onClick={onConfirmStarted}
              className="text-xs bg-green-600 text-white px-2.5 py-1 hover:bg-green-700 transition-colors">
              Confirm Started
            </button>
          )}
          {sheet.status === 'started' && (
            <button onClick={onRecordInvoice}
              className="text-xs bg-carriera-text text-white px-2.5 py-1 hover:bg-carriera-accent transition-colors">
              Record Invoice
            </button>
          )}
          {/* Also on an invoiced drop-out: the client may still pay, and the JTK report counts paid placements. */}
          {sheet.payment_status !== 'paid' && (sheet.status === 'invoiced' ||
            (sheet.status === 'dropped_out' && sheet.invoice_number && !sheet.refund_date && !sheet.non_billable)) && (
            <button onClick={onMarkPaid}
              className="text-xs bg-green-600 text-white px-2.5 py-1 hover:bg-green-700 transition-colors">
              Mark Paid
            </button>
          )}
          {sheet.status === 'invoiced' && sheet.payment_status === 'paid' && (
            <button onClick={onMarkComplete}
              className="text-xs bg-carriera-text text-white px-2.5 py-1 hover:bg-carriera-accent transition-colors">
              Mark Complete
            </button>
          )}

          {/* ··· menu */}
          <div className="ml-auto relative">
            <button onClick={() => setMenuOpen(o => !o)}
              className="text-carriera-muted hover:text-carriera-text text-sm px-1">···</button>
            {menuOpen && (
              <div className="absolute right-0 top-6 bg-white border border-carriera-border shadow-md z-10 w-40"
                onMouseLeave={() => setMenuOpen(false)}>
                {(sheet.status === 'profile_prepared' || sheet.status === 'profile_sent' || sheet.status === 'interview_scheduled') && (
                  <>
                    <button onClick={() => { setMenuOpen(false); onChangePosition(); }}
                      className="w-full text-left px-3 py-2 text-xs text-carriera-sec hover:bg-carriera-bg">Change position</button>
                    <button onClick={() => { setMenuOpen(false); onPlace(); }}
                      className="w-full text-left px-3 py-2 text-xs text-green-700 hover:bg-carriera-bg">Mark Placed</button>
                    <button onClick={() => { setMenuOpen(false); onReject(); }}
                      className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-carriera-bg">Mark Rejected</button>
                    <div className="border-t border-carriera-border" />
                  </>
                )}
                {isPostPlacement && sheet.status !== 'completed' && (
                  <button onClick={() => { setMenuOpen(false); onEditPlacement(); }}
                    className="w-full text-left px-3 py-2 text-xs text-carriera-sec hover:bg-carriera-bg">Edit placement details</button>
                )}
                {isPlacementStage && (
                  <>
                    <button onClick={() => { setMenuOpen(false); onMarkComplete(); }}
                      className="w-full text-left px-3 py-2 text-xs text-green-700 hover:bg-carriera-bg">Mark Complete</button>
                    <button onClick={() => { setMenuOpen(false); onDropout(); }}
                      className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-carriera-bg">Record Drop-out</button>
                    <div className="border-t border-carriera-border" />
                  </>
                )}
                <button onClick={() => { setMenuOpen(false); onToggleArchive(); }}
                  className="w-full text-left px-3 py-2 text-xs text-carriera-sec hover:bg-carriera-bg">Archive</button>
                <button onClick={() => { setMenuOpen(false); onDelete(); }}
                  className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-carriera-bg">Delete</button>
              </div>
            )}
          </div>
        </div>
      )}

      {sheet.archived && (
        <button onClick={onToggleArchive}
          className="text-xs text-carriera-muted hover:text-carriera-text transition-colors">
          Unarchive
        </button>
      )}
    </div>
  );
}

// ── Reusable Modal ────────────────────────────────
function Modal({ title, onClose, children, width = 'max-w-lg' }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white w-full ${width} max-h-screen overflow-y-auto`}
        style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-carriera-border">
          <h3 className="font-serif text-lg text-carriera-text">{title}</h3>
          <button onClick={onClose} className="text-carriera-muted hover:text-carriera-text text-xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}
